import { NavLink, useLocation } from '@/lib/router-compat';
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel,
  SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarMenuBadge,
} from '@/components/ui/sidebar';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { useAdminPendingCounts } from '@/hooks/useAdminPendingCounts';
import { destinationsFor, type NavAudience } from '@/lib/navigation';


export function AppSidebar() {
  const { user } = useAuth();
  const { isStaff, isViewer, isFounder, loading } = useUserRole();
  const { pathname } = useLocation();
  const { counts } = useAdminPendingCounts(!!user && !loading && (isStaff || isViewer));

  if (!user || loading) return null;
  const audience: NavAudience | null = isStaff ? 'staff' : isViewer ? 'viewer' : isFounder ? 'founder' : null;
  if (!audience) return null;
  // Founder pages already have their own accelerator menu; showing this one too
  // stacked two left-hand menus side by side.
  if (audience === 'founder' && (pathname.startsWith('/accelerator') || pathname === '/louisiana-technology-office')) return null;

  const destinations = destinationsFor(audience);
  const groups = Array.from(new Set(destinations.map((d) => d.group)));
  const isActive = (to: string) => pathname === to || (to !== '/' && pathname.startsWith(`${to}/`));

  return (
    <Sidebar collapsible="icon" className="hidden md:flex">
      <SidebarContent>
        {groups.map((group) => (
          <SidebarGroup key={group}>
            <SidebarGroupLabel>{group}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {destinations
                  .filter((d) => d.group === group)
                  .map((d) => (
                    <SidebarMenuItem key={d.to}>
                      <SidebarMenuButton asChild isActive={isActive(d.to)} tooltip={d.label}>
                        <NavLink to={d.to} className="flex items-center gap-2">
                          <d.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                          <span className="truncate">{d.label}</span>
                        </NavLink>
                      </SidebarMenuButton>
                      {(counts[d.to] ?? 0) > 0 && (
                        <SidebarMenuBadge className="bg-primary/15 text-primary rounded-full px-1.5" aria-label={`${counts[d.to]} pending`}>
                          {counts[d.to] > 99 ? '99+' : counts[d.to]}
                        </SidebarMenuBadge>
                      )}
                    </SidebarMenuItem>
                  ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </Sidebar>
  );
}
